import { ReactElement } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";
import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { formatDate, dateWeekday } from "~/utils";
import DisplayAbove from "~/components/styled/DisplayAbove";
import dynamic from "next/dynamic";
import { Heading } from "~/components/ui/Heading";
import { ContentWithSidebar } from "~/components/styled/ContentWithSidebar";
import { FlexibleContent } from "~/components/flexibleContent/FlexibleContent";
import StickySidebar from "~/components/sidebars/StickySidebar";
import SubMenuTwoCol from "~/components/styled/SubMenuTwoCol";
import { SidebarShare } from "~/components/sidebars/SidebarShare";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { ApiImage } from "~/components/ui/ApiImage";
import HeroSingleImage from "~/components/content/HeroSingleImage";
import { themeImgSizes } from "~/theme";
import { appConfig } from "~/config";
import styled from "styled-components";
import { BookTicket } from "~/components/ui/BookTicket";
import SideBarBlock from "~/components/sidebars/SideBarBlock";
import { SidebarBuyButtonWrapper } from "~/components/styled/SidebarBuyButtonWrapper";
import SafeHtmlDiv from "~/components/ui/SafeHtmlDiv";
import { Map } from "~/components/ui/Map";
import { Badge } from "~/components/ui/Badge";
import { RelatedContent } from "~/components/content/RelatedContent";
import { RelatedProducts } from "~/components/content/RelatedProducts";
//import HeroSlideShow from "~/components/content/HeroSlideShow";

const HeroSlideShow = dynamic(
  () => import("~/components/content/HeroSlideShow")
);

const Documents = dynamic(() => import("~/components/ui/Documents"));
const imgSizes = {
  tablet: {
    landscape: "90vw",
    portrait: "40vw",
  },
  mobileLandscape: {
    landscape: "100vw",
    portrait: "70vw",
  },
  base: {
    landscape: "120vw",
    portrait: "70vw",
  },
};

const SlideshowContainer = styled.div`
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: 100vh;
        min-height: 300px;
        max-height: min(
          calc(0.60 * (100vw - var(--sbw, 0px) - 4vw)), 
          calc(${props.theme.spacePx(breakpoint, 1)} + ${props.theme.spacePx(
        breakpoint,
        2
      )}), 
          calc(100vh - ${props.theme.spacePx(
            breakpoint,
            5
          )} - ${props.theme.spacePx(breakpoint, 3)})
        );
        
        `;
    })}
`;

const MapDiv = styled.div`
  margin: auto;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        padding-top: ${props.theme.spacePx(breakpoint, 8)};          
        
    `;
    })}
`;

const Badges = styled.div`
  grid-area: badges;
  display: flex;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          column-gap: ${props.theme.spacePx(breakpoint, 9)};          
        `;
    })}
`;

const Info = styled.div`
  display: grid;
  grid-template-areas:
    "badges"
    "text";

  ${({ theme }) => theme.breakpoints.tablet} {
    grid-template-columns: auto 1fr;
    grid-template-areas: "text badges";
    width: auto;
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          column-gap: ${props.theme.spacePx(breakpoint, 8)};
          padding-bottom: ${(
            props.theme.space(breakpoint, 7) - props.theme.space(breakpoint, 9)
          ).toFixed(2)}px;          
        `;
    })}
`;

const Captions = styled.div`
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      ${props.theme.textStyle(breakpoint, `caption`)};   
      text-transform: none;      
        `;
    })}
`;

const EventDate = styled.div``;

const EventDetail = ({ data }: { data: any }) => {
  const config = useConfigContext();

  // text-decoration: line-through;color: #666;
  const formattedDateBegin = formatDate(data?.acf?.date_begin);
  const formattedDateEnd = formatDate(data?.acf?.date_end);
  const now = new Date(new Date().setHours(0, 0, 0, 0));

  let pastEvent = false;

  try {
    if (typeof data?.acf?.date_end === "string" && data?.acf?.date_end.trim()) {
      const end = new Date(data?.acf?.date_end);

      pastEvent = end < now;
    }
  } catch (e) {}

  const exhibitionText = (
    <>
      EVENT
      {(formattedDateBegin || formattedDateEnd) && (
        <>
          &emsp;
          <span style={{ fontWeight: 400 }}>
            {formattedDateBegin}

            {formattedDateEnd && formattedDateBegin !== formattedDateEnd && (
              <>
                {formattedDateBegin && formattedDateEnd && <>&ndash;</>}
                {formattedDateEnd}
              </>
            )}

            {formattedDateBegin && data?.acf?.dates?.length === 1 && (
              <>
                {" "}
                / {data?.acf?.dates?.[0]?.time_begin}&ndash;
                {data?.acf?.dates?.[0]?.time_end}
              </>
            )}
          </span>
        </>
      )}
    </>
  );
  const eventCaption = (
    <Heading heading="h4" space={9} inline>
      {exhibitionText}
    </Heading>
  );

  const isOffsite =
    data?.taxonomies?.programme_category?.length > 0 &&
    data?.taxonomies?.programme_category?.reduce((flag: boolean, cat: any) => {
      if (cat.slug === "off-site") return true;
      return flag;
    }, false);

  const info = (
    <Info>
      <div style={{ gridArea: "text" }}>{eventCaption}</div>
      <Badges>
        {data?.acf?.digital_event && (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            DIGITAL EVENT
          </Badge>
        )}
        {isOffsite && (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            OFF-SITE
          </Badge>
        )}
        {pastEvent ? (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            PAST EVENT
          </Badge>
        ) : (
          <BookTicket
            space={8}
            heading="h4"
            data={data}
            digitalEvent={data?.acf?.digital_event}
          />
        )}
      </Badges>
    </Info>
  );

  // const credits =
  //   data.acf.hero_images?.length > 0
  //     ? data.acf.hero_images.reduce(
  //         (carry: string[], img: any, index: number) => {
  //           if (img?.caption && img.caption.trim()) {
  //             carry.push(`<b>${index + 1}.</b> ${img.caption.trim()}`);
  //           }
  //           return carry;
  //         },
  //         []
  //       )
  //     : [];

  let credits = null;
  if (data?.acf?.captions === "auto") {
    credits = (
      data.acf.hero_images?.length > 0
        ? data.acf.hero_images.reduce(
            (carry: string[], img: any, index: number) => {
              if (img?.caption && img.caption.trim()) {
                carry.push(`<b>${index + 1}.</b> ${img.caption.trim()}`);
              }
              return carry;
            },
            []
          )
        : []
    ).join(" ");
  } else if (
    data?.acf?.captions === "custom" &&
    (data?.acf?.customCaptions ?? "").trim()
  ) {
    credits = data?.acf?.customCaptions?.trim();
  }

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={`${data?.title} - ${
          data?.acf?.sub_title && data?.acf?.sub_title.trim()
            ? `${data?.acf?.sub_title} -`
            : ``
        }Event - ${config.appTitle}`}
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header>
        <SubMenuTwoCol alignSelf="center" colGapSpace={8}>
          <SubMenuTwoCol
            alignSelf="center"
            colGapSpace={8}
            gridTemplateColumns="auto 1fr"
          >
            <Heading space={7} heading="h3" inline trimmed html={data?.title} />

            <DisplayAbove breakpoint="tabletLandscape" inline>
              <Heading space={0} heading="h4" inline noWrap>
                {exhibitionText}
              </Heading>
            </DisplayAbove>
          </SubMenuTwoCol>
          <Heading space={7} heading="h3" inline>
            <Badges>
              {data?.acf?.digital_event && (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  DIGITAL EVENT
                </Badge>
              )}
              {isOffsite && (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  OFF-SITE
                </Badge>
              )}
              {pastEvent ? (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  PAST EVENT
                </Badge>
              ) : (
                <BookTicket
                  space={8}
                  heading="h4"
                  data={data}
                  digitalEvent={data?.acf?.digital_event}
                />
              )}
            </Badges>
          </Heading>
        </SubMenuTwoCol>
      </Header>
      <main id="content">
        {data?.acf?.hero_images?.length > 0 ? (
          data.acf.hero_images?.length > 1 ? (
            <SlideshowContainer>
              <HeroSlideShow
                hasYPadding
                key={`slideshow-${data.id}}`}
                slides={data.acf.hero_images.map((img: any, index: number) => (
                  <ApiImage
                    key={`slideshowimg-${data.id}-${index}`}
                    aspectRatioDirection="height"
                    alt={img?.alt}
                    sizes={img?.sizes}
                    imgSizes={themeImgSizes(imgSizes)}
                    captionIndex={
                      img.caption && img.caption.trim() ? index + 1 : undefined
                    }
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                ))}
              />
            </SlideshowContainer>
          ) : (
            <HeroSingleImage>
              <ApiImage
                bgColor="#fff"
                key={`slideshowimg-${data.id}`}
                aspectRatioDirection="width"
                alt={
                  data?.acf?.hero_images?.length > 0
                    ? data.acf.hero_images?.[0]?.alt
                    : ""
                }
                sizes={
                  data?.acf?.hero_images?.length > 0
                    ? data.acf.hero_images?.[0]?.sizes
                    : data?.featuredImage?.sizes
                }
                loading="eager"
                imgSizes={themeImgSizes(imgSizes)}
                mode="fill"
                objectFit="contain"
                objectPosition="left top"
                captionIndex={
                  data.acf.hero_images?.[0]?.caption &&
                  data.acf.hero_images?.[0]?.caption.trim()
                    ? 1
                    : undefined
                }
              />
            </HeroSingleImage>
          )
        ) : (
          <></>
        )}

        <PageMargins
          spaceTop={!data?.acf?.hero_images?.length ? 7 : undefined}
          spaceBottom={5}
        >
          {data?.acf?.hero_images?.length > 0 && <>{info}</>}

          <Heading
            asTag
            heading="h1"
            space={data?.acf?.sub_title.trim() ? 8 : 6}
            html={data?.title}
          />

          {data?.acf?.sub_title.trim() && (
            <Heading heading="h2" space={6} html={data?.acf?.sub_title} />
          )}

          <ContentWithSidebar>
            {data?.acf?.content?.length > 0 ? (
              <FlexibleContent
                columnWidth="wide"
                field="content"
                content={data.acf.content}
              />
            ) : (
              <div />
            )}

            <StickySidebar>
              <BookTicket
                space={7}
                heading="h3"
                data={data}
                fullWidth
                WrapWith={SidebarBuyButtonWrapper}
                digitalEvent={data?.acf?.digital_event}
              />

              {formattedDateBegin && data?.acf?.dates?.length > 0 && (
                <SideBarBlock>
                  <Heading heading="h4" asTag space={8}>
                    Event Date
                    {formattedDateBegin && data?.acf?.dates?.length > 1
                      ? "s"
                      : ""}
                  </Heading>
                  {pastEvent ? (
                    <div>This event has passed</div>
                  ) : (
                    <>
                      {formattedDateBegin && data?.acf?.dates?.length === 1 ? (
                        <div>
                          {dateWeekday(data?.acf?.dates[0].date)}{" "}
                          {formatDate(data?.acf?.dates[0].date, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                          <br />
                          {(data?.acf?.dates[0].time_begin ||
                            data?.acf?.dates[0].time_end) && (
                            <span>
                              {data?.acf?.dates[0].time_begin}&ndash;
                              {data?.acf?.dates[0].time_end}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div>
                          {formattedDateBegin &&
                            data?.acf?.dates
                              .sort((d1: any, d2: any) => {
                                if (d1.date > d2.date) return 1;
                                if (d1.date < d2.date) return -1;
                                return 0;
                              })
                              .filter((d: any) => {
                                try {
                                  const dateEnd = new Date(d?.date);
                                  return dateEnd >= now;
                                } catch (err: any) {}
                                return false;
                              })
                              .map((date: any, index: number) => {
                                return (
                                  <EventDate key={`event-date-${index}`}>
                                    {dateWeekday(date.date)}{" "}
                                    <b>{formatDate(date.date)}</b>
                                    {(date.time_begin || date.time_end) && (
                                      <span>
                                        {" "}
                                        / {date.time_begin}&ndash;
                                        {date.time_end}
                                      </span>
                                    )}
                                  </EventDate>
                                );
                              })}
                        </div>
                      )}
                    </>
                  )}
                </SideBarBlock>
              )}
              {(data?.acf?.date_further_information ||
                (data?.acf?.free_event === false &&
                  data?.acf?.ticket_cost)) && (
                <SideBarBlock>
                  <Heading heading="h4" asTag space={8}>
                    Event Details
                  </Heading>
                  {data?.acf?.date_further_information && (
                    <SafeHtmlDiv
                      html={`
                        ${
                          data?.acf?.free_event === false &&
                          data?.acf?.ticket_cost
                            ? `<p>${data?.acf?.ticket_cost}</p>`
                            : ""
                        }
                        ${data?.acf?.date_further_information}
                        `}
                    />
                  )}
                </SideBarBlock>
              )}

              {data?.acf?.venue &&
                data?.acf?.venue?.name &&
                data?.acf?.venue?.address && (
                  <SideBarBlock>
                    <Heading heading="h4" space={8}>
                      VENUE
                    </Heading>
                    <SafeHtmlDiv
                      html={`
                      <p><b>${data?.acf?.venue?.name.trim()}</b></p>
                      
                    `}
                    />
                    <SafeHtmlDiv html={data?.acf?.venue?.address} />

                    {data?.acf?.venue?.map?.lat && data?.acf?.venue?.map?.lng && (
                      <MapDiv>
                        <Map
                          lat={data?.acf?.venue?.map?.lat}
                          lng={data?.acf?.venue?.map?.lng}
                          zoom={14}
                          ratio="4 / 3"
                        />
                      </MapDiv>
                    )}
                  </SideBarBlock>
                )}

              {credits && (
                <SideBarBlock>
                  <Heading heading="h4" space={8}>
                    Image Credits
                  </Heading>
                  <Captions>
                    <SafeHtmlDiv html={credits} />
                  </Captions>
                </SideBarBlock>
              )}
              <SidebarShare
                title={data?.title}
                url={`${config.baseUrl}/event/${data?.slug}`}
              />
            </StickySidebar>
          </ContentWithSidebar>
        </PageMargins>

        {data?.acf?.documents?.length > 0 && (
          <Documents documents={data?.acf?.documents} />
        )}

        <RelatedProducts
          key={`event-products-${data?.id}`}
          id={data?.id}
          ids={data?.acf?.shop_items ?? []}
        />
        <RelatedContent
          key={`event-related-${data?.id}`}
          ids={[data?.id]}
          relatedItems={data?.acf?.related_content ?? []}
        />
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const events = await restApiESQuery({
    type: "event",
    perPage: 100,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: events?.data?.length
      ? events?.data.map((item: any) => item.url)
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  let data = await restApiGetPostBySlugOrFallbackId(
    "event",
    context?.params?.slug,
    token
  );

  if (!data) {
    data = await restApiGetPostBySlugOrFallbackId(
      "exhibition",
      context?.params?.slug
    );

    if (data) {
      return {
        redirect: {
          destination: `/exhibition/${context?.params?.slug}`,
          permanent: true,
        },
      };
    } else {
      const frontendSettings = await restApiESGetSettings([
        "programmeCategory",
      ]);
      return {
        props: {
          frontendSettings,
        },
        notFound: true,
        revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
      };
    }
  }

  const frontendSettings = await restApiESGetSettings(["programmeCategory"]);
  return {
    props: {
      frontendSettings,
      data,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("eventDetail", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

EventDetail.getLayout = function getLayout(page: ReactElement, props: any) {
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default EventDetail;
